<template>
  <ion-page>
    <ion-content v-if="lesson && lesson.pages" class="h-full">
      <!-- Commitment review overlay -->
      <div
        v-if="commitmentReview"
        class="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/70 px-6"
      >
        <div class="w-full max-w-md rounded-3xl bg-white p-8 text-center shadow-2xl">
          <div
            class="font-quicksand mb-2 text-sm font-semibold tracking-wide text-[#9b7b6a] uppercase"
          >
            You committed to:
          </div>
          <div class="font-quicksand mb-6 rounded-xl bg-[#fff7ef] p-4 text-[#5c3d2e] italic">
            "{{ commitmentReview.text }}"
          </div>
          <template v-if="commitmentResult === null">
            <div class="font-quicksand mb-4 font-semibold text-[#5c3d2e]">
              Did you follow through?
            </div>
            <div class="flex flex-col gap-3">
              <button
                class="font-quicksand w-full rounded-xl bg-[#96bf5d] py-3 font-semibold text-white shadow transition hover:brightness-105 active:brightness-95"
                @click="resolveCommitment(true)"
              >
                Yes, I did it!
              </button>
              <button
                class="font-quicksand w-full rounded-xl bg-[#e0c9b8] py-3 font-semibold text-[#5c3d2e] shadow transition hover:brightness-105 active:brightness-95"
                @click="resolveCommitment(false)"
              >
                Not quite...
              </button>
            </div>
          </template>
          <template v-else-if="commitmentResult === 'yes'">
            <div class="mb-2 text-4xl">🎉</div>
            <div class="font-quicksand mb-4 font-semibold text-[#5c3d2e]">
              Amazing — you showed up for yourself!
            </div>
            <button
              class="font-quicksand w-full rounded-xl bg-[#96bf5d] py-3 font-semibold text-white shadow transition hover:brightness-105 active:brightness-95"
              @click="dismissCommitmentReview"
            >
              Continue
            </button>
          </template>
          <template v-else>
            <div class="mb-2 text-4xl">💛</div>
            <div class="font-quicksand mb-4 text-[#5c3d2e]">
              That's okay. Growth isn't always linear — what matters is that you're still here,
              still trying. Every small step counts.
            </div>
            <button
              class="font-quicksand w-full rounded-xl bg-[#caa37b] py-3 font-semibold text-white shadow transition hover:brightness-105 active:brightness-95"
              @click="dismissCommitmentReview"
            >
              Continue
            </button>
          </template>
        </div>
      </div>

      <template v-for="pageData in [lesson.pages[page]]" :key="pageData.id">
        <div class="flex h-full flex-col">
          <round-header
            image-type="full"
            :image="lesson.image"
            :subtitle="lesson.name"
            :title="completed ? 'Completed' : pageData.name"
            disable-edit-mode="true"
            class="h-30 overflow-hidden"
            :back="`/courses/${courseId}`"
          ></round-header>
          <div
            id="container"
            class="flex flex-grow flex-col items-center justify-start gap-4 pt-10"
            style="
              background:
                linear-gradient(rgba(125, 125, 125, 0.5), rgba(125, 125, 125, 0.5)),
                url('https://db.retry.li/assets/fb8064a5-2f01-40dc-bcf0-5a71c04ce305?width=256');
            "
          >
            <div
              v-if="completed || pageData.image"
              class="w-100 max-w-[90%] rounded-4xl border-4 border-[#ffffff33] shadow-2xl transition-all duration-500"
              :style="`background: url('https://db.retry.li/assets/${completed ? '2b6d37b4-b26e-4ca0-b47e-5b7a22046c7b' : pageData.image}?width=256'); background-size: cover; background-position: center;`"
              :class="!completed && pageData.text.length > 300 ? 'h-75' : 'h-100'"
            ></div>
            <template v-if="!completed">
              <div class="w-[768px] max-w-[90%]">
                <div
                  class="font-quicksand p-4 text-center text-white text-shadow-md [&>p]:mb-4"
                  v-html="pageData.text"
                ></div>
                <template v-if="pageData.type == 'notebook' || pageData.type == 'commitment'">
                  <textarea
                    v-model="inputString"
                    rows="6"
                    :placeholder="
                      pageData.type == 'commitment'
                        ? pageData.input_hint || 'Write your commitment for tomorrow…'
                        : pageData.input_hint
                    "
                    class="font-quicksand w-full resize-none rounded-xl border border-[#d8b99b] bg-[#fff7ef] p-4 text-base text-[#5c3d2e] shadow-inner focus:ring-2 focus:ring-[#caa37b] focus:outline-none"
                  ></textarea>
                </template>
              </div>
              <hold-to-continue-button
                class="transition-all duration-500"
                :class="
                  showContinue && canContinue() ? 'opacity-100' : 'pointer-events-none opacity-0'
                "
                @charged="nextPage"
              />
            </template>
            <template v-else>
              <div class="w-[768px] max-w-[90%]">
                <div class="font-quicksand p-4 text-center text-4xl text-white text-shadow-md">
                  Lesson completed!
                </div>
              </div>
            </template>
          </div>
        </div>
      </template>
    </ion-content>
  </ion-page>
</template>

<script setup lang="ts" async>
import { IonContent, IonPage } from '@ionic/vue';
import RoundHeader from '../../components/RoundHeader.vue';
import { directus } from '../../services/Directus.ts';
import { readItem, sleep } from '@directus/sdk';
import { onMounted, ref, type Ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import HoldToContinueButton from '../../components/HoldToContinueButton.vue';
import { doc, getDoc, getFirestore, setDoc } from 'firebase/firestore';
import { useCurrentUser } from 'vuefire';
import { CourseProgress } from '../../models/CourseProgress.ts';
import type { CommitmentData } from '../../models/CourseProgress.ts';
import confetti from 'canvas-confetti';
import { Preferences } from '@capacitor/preferences';

const page: Ref<number> = ref<number>(0);
const route = useRoute();
const courseId = route.params.courseId as string;
const lessonId = route.params.lessonId as string;
const course: Ref<any> = ref({});
const lesson: Ref<any> = ref({});
const completed: Ref<boolean> = ref(false);
const showContinue: Ref<boolean> = ref(false);
const user = useCurrentUser();
const db = getFirestore();
const router = useRouter();
const inputString: Ref<string> = ref<string>('');
const commitmentReview: Ref<CommitmentData | null> = ref(null);
const commitmentResult: Ref<'yes' | 'no' | null> = ref(null);
const previousLessonId: Ref<number | null> = ref(null);

function canContinue(): boolean {
  const pageData = lesson.value.pages[page.value];
  if (pageData.type == 'notebook' || pageData.type == 'commitment') {
    if (!inputString.value) return false;
  }
  return true;
}

function hasNextPage(): boolean {
  if (!lesson.value?.pages) return false;
  return lesson.value.pages.length - 1 > page.value;
}

async function loadPage() {
  showContinue.value = false;
  const result = await Preferences.get({ key: `LessonInput.${lessonId}` });
  inputString.value = result?.value ?? '';
  setTimeout(() => {
    showContinue.value = true;
  }, lesson.value.pages[page.value].text.length * 15);
}

async function nextPage() {
  const pageData = lesson.value.pages[page.value];
  const committedText = inputString.value;

  if (committedText) {
    await Preferences.set({ key: `LessonInput.${lessonId}`, value: committedText });
  }

  if (!hasNextPage()) {
    completed.value = true;
    inputString.value = '';
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
    });

    const docRef = doc(db, `users/${user.value!.uid}/courses/${course.value.id}`);
    const document = await getDoc(docRef);
    const savedData = document.data();
    const courseProgress = new CourseProgress();
    if (savedData) Object.assign(courseProgress, savedData);
    courseProgress.finishedLesson(lesson.value.id);
    if (pageData.type === 'commitment' && committedText) {
      courseProgress.addCommitment(
        lesson.value.id,
        committedText,
        pageData.unlock_after_hours ?? 24
      );
    }
    await setDoc(docRef, courseProgress.toPlain());
    await sleep(3000);
    await router.push(`/courses/${course.value.id}`);
    return;
  }

  if (pageData.type === 'commitment' && committedText) {
    const docRef = doc(db, `users/${user.value!.uid}/courses/${course.value.id}`);
    const snapshot = await getDoc(docRef);
    const courseProgress = new CourseProgress();
    if (snapshot.data()) Object.assign(courseProgress, snapshot.data());
    courseProgress.addCommitment(lesson.value.id, committedText, pageData.unlock_after_hours ?? 24);
    await setDoc(docRef, courseProgress.toPlain());
  }

  page.value += 1;
  await loadPage();
}

async function resolveCommitment(fulfilled: boolean) {
  commitmentResult.value = fulfilled ? 'yes' : 'no';

  if (fulfilled) {
    confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 } });
  }

  if (previousLessonId.value !== null) {
    const docRef = doc(db, `users/${user.value!.uid}/courses/${course.value.id}`);
    const snapshot = await getDoc(docRef);
    const courseProgress = new CourseProgress();
    if (snapshot.data()) Object.assign(courseProgress, snapshot.data());
    courseProgress.fulfillCommitment(previousLessonId.value, fulfilled);
    await setDoc(docRef, courseProgress.toPlain());
  }
}

function dismissCommitmentReview() {
  commitmentReview.value = null;
  commitmentResult.value = null;
}

onMounted(async () => {
  course.value = await directus.request(
    readItem('courses', courseId, { fields: ['*', 'lessons.lessons_id.id'] })
  );
  lesson.value = await directus.request(
    readItem('lessons', lessonId, {
      fields: ['*', 'pages.*'],
    })
  );

  if (user.value) {
    const docRef = doc(db, `users/${user.value.uid}/courses/${course.value.id}`);
    const snapshot = await getDoc(docRef);
    const savedData = snapshot.data() ?? {};

    const lessonIndex =
      course.value.lessons?.findIndex((l: any) => l.lessons_id.id == lessonId) ?? -1;
    if (lessonIndex > 0) {
      const prevLesson = course.value.lessons[lessonIndex - 1];
      const prevId: number = prevLesson.lessons_id.id;
      previousLessonId.value = prevId;
      const commitment = savedData.commitments?.[prevId];
      if (
        commitment &&
        commitment.fulfilled === null &&
        new Date() >= new Date(commitment.unlocksAt)
      ) {
        commitmentReview.value = commitment;
      }
    }
  }

  await loadPage();
});
</script>
